import { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { GitRepo, ImportResult } from "../../types/cloud";
import { importRepo } from "../../lib/api/cloud";

interface ImportDialogProps {
  open: boolean;
  repo: GitRepo | null;
  onClose: () => void;
  onSuccess: (result: ImportResult) => void;
}

const PROJECT_NAME_RE = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

function sanitizeProjectName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 30);
}

type DialogState = "form" | "importing" | "success" | "error";

export function ImportDialog({ open, repo, onClose, onSuccess }: ImportDialogProps) {
  const { t } = useTranslation();
  const [projectName, setProjectName] = useState("");
  const [branch, setBranch] = useState("");
  const [state, setState] = useState<DialogState>("form");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  function validateProjectName(name: string): string | null {
    if (name.length < 3) return t('dashboard.importDialog.projectNameMinLength');
    if (name.length > 30) return t('dashboard.importDialog.projectNameMaxLength');
    if (!PROJECT_NAME_RE.test(name)) return t('dashboard.importDialog.projectNameInvalidFormat');
    return null;
  }

  useEffect(() => {
    if (open && repo) {
      setProjectName(sanitizeProjectName(repo.name));
      setBranch(repo.defaultBranch);
      setState("form");
      setError(null);
      setResult(null);
    }
  }, [open, repo]);

  if (!open || !repo) return null;

  const nameError = validateProjectName(projectName);
  const canSubmit = state === "form" && !nameError && projectName.length >= 3;

  const handleImport = async () => {
    if (!canSubmit) return;
    setState("importing");
    setError(null);

    try {
      const importResult = await importRepo({
        provider: repo.provider,
        owner: repo.owner,
        repo: repo.name,
        projectName: projectName !== sanitizeProjectName(repo.name) ? projectName : undefined,
        branch: branch !== repo.defaultBranch ? branch : undefined,
      });
      setResult(importResult);
      setState("success");
      onSuccess(importResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.importDialog.importFailed'));
      setState("error");
    }
  };

  const providerIcon = repo.provider === "github" ? "\uD83D\uDC19" : "\uD83D\uDCE6";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-lg">{providerIcon}</span>
          <h3 className="text-lg font-semibold text-[#f5f5f5]">
            {t('dashboard.importDialog.importTitle', { owner: repo.owner, repo: repo.name })}
          </h3>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-2 mb-4">
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
            {repo.provider}
          </span>
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${
              repo.isPrivate ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"
            }`}
          >
            {repo.isPrivate ? t('dashboard.repo.private') : t('dashboard.repo.public')}
          </span>
        </div>

        {state === "success" && result ? (
          /* Success state */
          <div>
            <div className="flex items-center gap-2 mb-3 p-3 bg-green-500/5 border border-green-500/20 rounded">
              <svg
                className="w-5 h-5 text-green-400 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-green-400 text-sm">
                <Trans
                  i18nKey="dashboard.importDialog.importSuccess"
                  values={{ projectName: result.projectName }}
                  components={{ bold: <span className="font-semibold" /> }}
                />
              </span>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[#999] hover:text-[#f5f5f5] transition-colors"
              >
                {t('dashboard.importDialog.close')}
              </button>
              <a
                href={result.instanceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white rounded-lg text-sm font-medium transition-all"
              >
                {t('dashboard.importDialog.openCloseCode ADE')}
              </a>
            </div>
          </div>
        ) : (
          /* Form / Importing / Error states */
          <div>
            {/* Project name */}
            <div className="mb-4">
              <label className="block text-[#999] text-xs mb-1">{t('dashboard.importDialog.projectName')}</label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                disabled={state === "importing"}
                className="w-full px-3 py-2 bg-[#111] border border-[#333] rounded text-[#f5f5f5] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 disabled:opacity-50"
                placeholder={t('dashboard.importDialog.projectNamePlaceholder')}
                autoFocus
              />
              {projectName.length > 0 && nameError && (
                <p className="text-red-400 text-xs mt-1">{nameError}</p>
              )}
            </div>

            {/* Branch */}
            <div className="mb-4">
              <label className="block text-[#999] text-xs mb-1">{t('dashboard.importDialog.branch')}</label>
              <input
                type="text"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                disabled={state === "importing"}
                className="w-full px-3 py-2 bg-[#111] border border-[#333] rounded text-[#f5f5f5] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 disabled:opacity-50"
                placeholder={t('dashboard.importDialog.branchPlaceholder')}
              />
            </div>

            {/* Error message */}
            {state === "error" && error && (
              <div className="mb-4 p-3 bg-red-500/5 border border-red-500/20 rounded text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2 border-t border-[#333]">
              <button
                onClick={onClose}
                disabled={state === "importing"}
                className="px-4 py-2 text-sm font-medium text-[#999] hover:text-[#f5f5f5] transition-colors disabled:opacity-40"
              >
                {t('dashboard.importDialog.cancel')}
              </button>
              <button
                onClick={handleImport}
                disabled={!canSubmit && state !== "error"}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {state === "importing" && (
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {state === "importing"
                  ? t('dashboard.importDialog.cloningRepo')
                  : state === "error"
                    ? t('dashboard.importDialog.retryImport')
                    : t('dashboard.importDialog.importButton')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
